import {AccountExternal, AccountSwitcherItem, ChargeOffType, Vertical} from "@bounce/shared/consumer/declarations";
import {prettifyCurrency} from "@bounce/shared/helpers";

type AccountDetail = {
  label: string;
  value: string;
  isDate?: boolean;
};

type VerticalDetailsSource = {
  vertical?: Vertical | null;
  product?: string | null;
  card_last_4_digits?: string | null;
};

export const getVerticalDetails = ({vertical, product, card_last_4_digits}: VerticalDetailsSource): AccountDetail[] => {
  if (vertical === Vertical.CREDIT_CARD && card_last_4_digits) {
    return [{label: "Card ending in", value: card_last_4_digits}];
  }
  if ((vertical === Vertical.BNPL || vertical === Vertical.POS) && product) {
    return [{label: "Type of debt", value: product}];
  }
  return [];
};

type AccountInfoItem = {
  text: string;
  boldText: string;
  boldTextAfter?: string;
  date?: string | Date;
};

export const getAccountDetails = (account: AccountSwitcherItem): AccountDetail[] => {
  const postChargeOff = account.charge_off_type === ChargeOffType.POST_CHARGE_OFF ? account : undefined;
  const details: AccountDetail[] = getVerticalDetails(account);

  if (postChargeOff?.current_creditor) {
    details.push({label: "Current creditor", value: postChargeOff.current_creditor});
  }

  if (postChargeOff?.original_creditor) {
    details.push({label: "Original creditor", value: postChargeOff.original_creditor});
  }

  if (account.origination_date) {
    details.push({label: "Open date", value: account.origination_date, isDate: true});
  }

  if (account.external_account_id) {
    details.push({label: "Account number", value: account.external_account_id});
  }

  if (account.public_id) {
    details.push({label: "Reference number", value: account.public_id});
  }

  return details;
};

export const getAccountInfoItems = (label: string, account: AccountSwitcherItem): AccountInfoItem[] => {
  const {
    active_payment_plan,
    lowest_monthly_installment_size,
    lowest_hardship_installment_size,
    one_time_installment_size,
  } = account;
  const lowestInstallmentSize = lowest_hardship_installment_size ?? lowest_monthly_installment_size;
  const preChargeOff = account.charge_off_type === ChargeOffType.PRE_CHARGE_OFF ? account : undefined;
  const items: AccountInfoItem[] = [];

  if (["No plan yet", "Dispute open", "Credit report risk"].includes(label)) {
    if (preChargeOff?.latest_dpd) {
      items.push({boldText: `${preChargeOff.latest_dpd} `, text: "days past due"});
      return items;
    }
    if (active_payment_plan?.total_paid) {
      items.push({boldText: prettifyCurrency(active_payment_plan.total_paid), text: " paid (Temporary plan)"});
    }
    if (lowestInstallmentSize) {
      items.push({
        text: "Plans from ",
        boldText: "",
        boldTextAfter: `${prettifyCurrency(lowestInstallmentSize)}/mo`,
      });
    } else if (one_time_installment_size) {
      items.push({text: "Resolve now for ", boldText: "", boldTextAfter: prettifyCurrency(one_time_installment_size)});
    }
    return items;
  }

  if (label === "Payment failed" && active_payment_plan?.late_installment) {
    items.push({
      text: "Late installment amount: ",
      boldText: "",
      boldTextAfter: prettifyCurrency(active_payment_plan.late_installment.amount_cents),
    });
    items.push({text: "Was due on ", boldText: "", date: active_payment_plan.late_installment.due_date});
    return items;
  }

  if (label === "Resolved" && active_payment_plan) {
    items.push({
      text: "Total balance paid ",
      boldText: "",
      boldTextAfter: prettifyCurrency(active_payment_plan.total_paid),
    });
    return items;
  }

  if (label === "Temporary plan" && active_payment_plan?.next_installment_size) {
    items.push({
      boldText: `${active_payment_plan.number_of_installments}`,
      text: " payments of ",
      boldTextAfter: prettifyCurrency(active_payment_plan.next_installment_size),
    });
    if (active_payment_plan.next_due_date) {
      items.push({text: "Next due date: ", boldText: "", date: active_payment_plan.next_due_date});
    }
    return items;
  }

  if (label === "Active plan" && active_payment_plan) {
    items.push({
      boldText: prettifyCurrency(active_payment_plan.total_paid),
      text: " paid out of ",
      boldTextAfter: prettifyCurrency(active_payment_plan.total_amount),
    });
    if (active_payment_plan.next_installment_size && active_payment_plan.next_due_date) {
      items.push({
        text: "Next ",
        boldText: "",
        boldTextAfter: `${prettifyCurrency(active_payment_plan.next_installment_size)} on `,
        date: active_payment_plan.next_due_date,
      });
    }
    return items;
  }

  return items;
};

export const isNotCollectible = (account?: Pick<AccountExternal, "status">) => {
  const inCreditorRecalled = account?.status === "creditor_recalled";
  const accountClosed = account?.status === "closed";

  return inCreditorRecalled || accountClosed;
};
